/* Login — email + password (with "Forgot password?" magic-link fallback).
   Used by paid customers (Kit one-time + Continuity subscribers) to access
   their /member dashboard. No site nav — clean dedicated auth page. */
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Mail, Lock, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiLogin, apiMe, apiForgotPassword, formatApiErrorDetail, getToken } from "@/lib/auth";

export default function Login() {
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState("login"); // "login" | "forgot"
    const [forgotSent, setForgotSent] = useState(false);

    // If already authenticated, jump to /member
    useEffect(() => {
        if (!getToken()) return;
        (async () => {
            try {
                await apiMe();
                nav("/member", { replace: true });
            } catch { /* not authed, stay */ }
        })();
    }, [nav]);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!email || !password) {
            toast.error("Enter your email and password");
            return;
        }
        try {
            setBusy(true);
            await apiLogin({ email: email.trim().toLowerCase(), password });
            toast.success("Welcome back");
            const next = new URLSearchParams(loc.search).get("next") || "/member";
            nav(next, { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
        } finally { setBusy(false); }
    };

    const sendForgot = async (e) => {
        e?.preventDefault?.();
        if (!email) {
            toast.error("Enter your email first");
            return;
        }
        try {
            setBusy(true);
            await apiForgotPassword({ email: email.trim().toLowerCase(), origin_url: window.location.origin });
            setForgotSent(true);
            toast.success("If that email is registered, a reset link is on its way.");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not send reset link");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-cafe-cream flex flex-col">
            <header className="px-4 sm:px-6 py-4 border-b border-border">
                <Link to="/" className="font-serif text-xl text-cafe-espresso hover:text-cafe-olive" data-testid="login-logo-link">
                    kaily<span className="text-cafe-terracotta">.</span>
                </Link>
            </header>

            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold mb-2">Member Hub</p>
                    <h1 className="font-serif text-3xl sm:text-4xl text-cafe-espresso mb-2">
                        {mode === "login" ? "Sign in." : "Reset your password."}
                    </h1>
                    <p className="text-sm text-cafe-mocha mb-8">
                        {mode === "login"
                            ? "Use the email you paid with."
                            : "We'll email you a one-hour reset link."}
                    </p>

                    {mode === "login" ? (
                        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                            <Field icon={<Mail className="h-4 w-4 text-cafe-mocha" />} label="Email">
                                <input
                                    type="email"
                                    autoFocus
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@cafe.com"
                                    className="w-full bg-transparent outline-none text-sm py-2.5 text-cafe-espresso placeholder:text-cafe-mocha/60"
                                    data-testid="login-email-input"
                                />
                            </Field>
                            <Field icon={<Lock className="h-4 w-4 text-cafe-mocha" />} label="Password">
                                <input
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Your password"
                                    className="w-full bg-transparent outline-none text-sm py-2.5 text-cafe-espresso placeholder:text-cafe-mocha/60"
                                    data-testid="login-password-input"
                                />
                            </Field>
                            <button
                                type="submit"
                                disabled={busy}
                                className="w-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] flex items-center justify-center gap-2 disabled:opacity-50"
                                data-testid="login-submit-btn"
                            >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setMode("forgot"); setForgotSent(false); }}
                                className="w-full text-xs text-cafe-mocha hover:text-cafe-olive underline underline-offset-4"
                                data-testid="login-forgot-link"
                            >
                                Forgot your password?
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={sendForgot} className="space-y-4" data-testid="forgot-form">
                            <Field icon={<Mail className="h-4 w-4 text-cafe-mocha" />} label="Email">
                                <input
                                    type="email"
                                    autoFocus
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@cafe.com"
                                    className="w-full bg-transparent outline-none text-sm py-2.5"
                                    data-testid="forgot-email-input"
                                />
                            </Field>
                            <button
                                type="submit"
                                disabled={busy}
                                className="w-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-6 py-3.5 text-sm font-bold uppercase tracking-[0.14em] flex items-center justify-center gap-2 disabled:opacity-50"
                                data-testid="forgot-submit-btn"
                            >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send reset link <ArrowRight className="h-4 w-4" /></>}
                            </button>
                            {forgotSent && (
                                <p className="text-xs text-cafe-olive text-center" data-testid="forgot-sent-note">
                                    Check your inbox. Link valid for 1 hour.
                                </p>
                            )}
                            <button
                                type="button"
                                onClick={() => setMode("login")}
                                className="w-full text-xs text-cafe-mocha hover:text-cafe-olive underline underline-offset-4"
                            >
                                Back to sign in
                            </button>
                        </form>
                    )}

                    <div className="mt-10 pt-6 border-t border-border text-xs text-cafe-mocha text-center">
                        New here?{" "}
                        <Link to="/ig/content/AI" className="text-cafe-olive hover:underline">
                            Browse IG Content AI →
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
}

function Field({ icon, label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mb-1.5 flex items-center gap-1.5">{icon}{label}</span>
            <div className="border border-border focus-within:border-cafe-olive rounded-xl px-3 bg-white transition-colors">
                {children}
            </div>
        </label>
    );
}
